<template>
  <background-particles />
  <v-container
    fluid
    class="fill-height bg-primary"
  >
    <v-row class="d-flex align-center justify-center">
      <v-col
        cols="12"
        md="4"
      >
        <v-card
          v-motion-roll-left
          class="pa-4"
          elevation="24"
        >
          <v-card-title class="text-primary text-center">
            <v-img
              src="@/assets/eye-academy-logo-light.svg"
              max-height="20"
              contain
            ></v-img>
            <Vue3Lottie
              class="my-6"
              :animationData="WelcomeJSON"
              :height="20"
              :width="250"
            />
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                class="text-center mt-6"
              >
                <div class="text-h6">Thank you for purchasing from us!</div>
                <div class="text-body-2 mt-4">
                  {{
                    setPasswordMode
                      ? "Please use the same email you paid with."
                      : "To setup your account, you can either sign in with Google or create a password."
                  }}
                </div>
              </v-col>

              <v-col
                cols="12"
                class="text-center mt-6 px-8"
                v-if="!setPasswordMode"
              >
                <v-img
                  id="google-login"
                  @click="loginGoogle"
                  v-ripple="{ class: 'text-red' }"
                  class="rounded"
                  src="@/assets/google_icon.svg"
                  max-height="45"
                  contain
                ></v-img>
              </v-col>
              <v-col
                cols="12"
                v-if="!setPasswordMode"
              >
                <div
                  style="width: 100%"
                  class="text-center"
                >
                  <table width="100%">
                    <td>
                      <hr />
                    </td>
                    <td
                      style="width: 1px; padding: 0 10px; white-space: nowrap"
                    >
                      Or
                    </td>
                    <td>
                      <hr />
                    </td>
                  </table>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="text-center px-8"
                v-if="!setPasswordMode"
              >
                <v-btn
                  @click="setPasswordMode = true"
                  color="primary"
                  block
                  rounded="lg"
                  variant="flat"
                >
                  Create a Password
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                class="text-center mt-4"
                v-if="setPasswordMode"
              >
                <v-text-field
                  v-model="email"
                  density="compact"
                  clearable
                  autocomplete="email"
                  type="email"
                  label="Email"
                ></v-text-field>
                <v-text-field
                  v-model="password"
                  density="compact"
                  @click:append="togglePassword"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  clearable
                  autocomplete="password"
                  label="Password"
                ></v-text-field>
                <v-text-field
                  v-model="cpassword"
                  density="compact"
                  @click:append="togglePassword"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  clearable
                  autocomplete="password"
                  label="Confirm Password"
                ></v-text-field>
                <div class="text-caption text-left px-4">
                  <div>Password Rules:</div>
                  <div
                    v-for="(pc, idx) in passwordChecks"
                    :key="idx"
                  >
                    <v-icon
                      class="mr-2"
                      :color="pc.valid ? 'green' : 'red'"
                      >{{
                        pc.valid ? "mdi-check-circle" : "mdi-close-circle"
                      }}</v-icon
                    >
                    {{ pc.text }}
                  </div>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="text-center mt-4"
                v-if="setPasswordMode"
              >
                <v-btn
                  @click="submit"
                  :disabled="!registerFormValid"
                  :loading="loading"
                  color="primary"
                  block
                  rounded="lg"
                  variant="flat"
                >
                  Submit
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                class="mt-12"
              >
                <v-divider> </v-divider>
              </v-col>
              <v-col
                cols="12"
                class="text-center"
              >
                <v-btn
                  @click="router.push('/login')"
                  color="primary"
                  block
                  rounded="lg"
                  variant="text"
                >
                  Already have an account? Sign in
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                class="text-center px-8"
                v-if="setPasswordMode"
              >
                <v-btn
                  @click="setPasswordMode = false"
                  color="primary"
                  block
                  rounded="lg"
                  variant="text"
                >
                  Back
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
  import { ref, computed, reactive } from "vue";
  import WelcomeJSON from "@/assets/welcome.json";
  import BackgroundParticles from "@/components/BackgroundParticles.vue";
  import ForgotPassword from "@/components/ForgotPassword.vue";
  import { useAlertStore } from "@/store/alert";
  const alertStore = useAlertStore();

  import { getFunctions, httpsCallable } from "firebase/functions";
  import {
    signInWithPopup,
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
  } from "firebase/auth";

  import { useUserStore } from "@/store/user";
  const userStore = useUserStore();

  import { useFirebaseAuth } from "vuefire";
  const auth = useFirebaseAuth();
  const provider = new GoogleAuthProvider();
  const functions = getFunctions();
  functions.region = "europe-west2";
  const setupAccount = httpsCallable(functions, "setupAccount");

  const loading = ref(false);

  const email = ref("");
  const password = ref("");
  const cpassword = ref("");
  const showPassword = ref(false);
  const togglePassword = () => (showPassword.value = !showPassword.value);

  // password validation logic
  import { usePasswordCheck } from "@/composables/passwordCheck";
  const { passwordChecks, registerFormValid } = usePasswordCheck(
    email,
    password,
    cpassword
  );

  const setPasswordMode = ref(false);

  import { useRouter, useRoute } from "vue-router";
  const router = useRouter();
  const route = useRoute();

  // get email from query param
  const emailParam = route.query.email;
  if (emailParam) {
    email.value = emailParam;
  }

  const loginGoogle = () => {
    signInWithPopup(auth, provider)
      .then(async (result) => {
        await userStore.getBasicUserData();
        if (userStore.basic.onboarded) {
          router.replace("/");
        } else {
          router.replace("/onboarding");
        }
      })
      .catch((error) => {
        console.error("Error with Google login: ", error);
        alertStore.showAlert(
          "There was an error logging you in. Please try again or contact support.",
          "error"
        );
      });
  };

  const submit = async () => {
    loading.value = true;
    const result = await setupAccount({
      email: email.value,
      password: password.value,
    });

    const { success, user } = result.data;

    if (success) {
      // Log user in with email/password
      await signInWithEmailAndPassword(auth, email.value, password.value);
      await userStore.getBasicUserData();
      router.replace("/onboarding");
    } else if (!success && user) {
      // User exists with providers, redirect to login
      alertStore.showAlert(
        "This account already exists. Please log in instead or use 'Forgot Password'.",
        "info"
      );
      router.replace("/login");
    } else {
      // No user found after timeout
      alertStore.showAlert(
        "Account not found. Please try again or contact support.",
        "error"
      );
    }

    loading.value = false;
  };
</script>

<style>
  #google-login {
    border: rgb(var(--v-theme-error)) 1px solid;
    cursor: pointer;
  }

  #google-login:hover {
    background-color: rgba(var(--v-theme-error), 0.1);
  }
</style>
